import React, { useEffect, useState } from 'react';
import { Paper, Typography, Box, CircularProgress, LinearProgress, Tooltip } from '@mui/material';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import api from '../../services/api';
import DeltaChip from './DeltaChip';

const RANGE_LABEL = { '24h': 'últimas 24h', '7d': 'últimos 7 dias', '30d': 'últimos 30 dias' };

/**
 * Top 5 usuários por sessões no range. Mostra delta por user (ícone trending).
 */
export default function TopUsersCard({ range = '24h' }) {
    const [state, setState] = useState({ loading: true, items: [], delta_pct: 0 });

    useEffect(() => {
        setState((s) => ({ ...s, loading: true }));
        api.get(`/dashboard/driade/top_users?range=${range}`)
            .then((res) => setState({ ...res.data, loading: false }))
            .catch(() => setState({ loading: false, items: [], delta_pct: 0 }));
    }, [range]);

    const max = Math.max(1, ...state.items.map((i) => i.count));

    return (
        <Paper
            elevation={2}
            sx={{
                m: 2,
                p: 2,
                minHeight: 280,
                display: 'flex',
                flexDirection: 'column',
                borderLeft: '4px solid #e01b29',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LeaderboardIcon color="primary" />
                    <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Top usuários — {RANGE_LABEL[range]}
                    </Typography>
                </Box>
                <DeltaChip value={state.delta_pct} />
            </Box>

            {state.loading ? (
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={28} />
                </Box>
            ) : state.items.length === 0 ? (
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Sem sessões no período
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                    {state.items.map((u, i) => {
                        const userDelta = u.prev_count
                            ? Math.round(((u.count - u.prev_count) / u.prev_count) * 100)
                            : (u.count > 0 ? 100 : 0);
                        const trending = userDelta > 5 ? 'up' : userDelta < -5 ? 'down' : 'flat';
                        return (
                            <Box key={u.username || i}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontFamily: 'Montserrat', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {i + 1}. {u.username || '(vazio)'}
                                        </Typography>
                                        {trending === 'up' && (
                                            <Tooltip title={`+${userDelta}% vs anterior`}>
                                                <TrendingUpIcon fontSize="small" color="success" />
                                            </Tooltip>
                                        )}
                                        {trending === 'down' && (
                                            <Tooltip title={`${userDelta}% vs anterior`}>
                                                <TrendingDownIcon fontSize="small" color="error" />
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, ml: 1 }}>
                                        {u.count.toLocaleString('pt-BR')}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, (u.count / max) * 100)}
                                    sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        bgcolor: '#f0f0f0',
                                        '& .MuiLinearProgress-bar': { bgcolor: '#e01b29' },
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Paper>
    );
}
